/** Scripts pour le bouton « Retour au sommet (de la page). » */

import { pipe } from "@mobily/ts-belt";
import { head as arrayHead } from "@mobily/ts-belt/Array";
import { tap as optionTap } from "@mobily/ts-belt/Option";

import {
  ATTRIBUT_ACTIF,
  ATTRIBUT_ARIA_HIDDEN,
  ATTRIBUT_TABINDEX,
  SELECTEUR_BOUTON_MENU_MOBILE,
} from "./constantes/dom";
import { recupereEleOuLeve } from "./lib/utils";

/**
 * 1. Récupérer la hauteur de l'écran et la hauteur de la page.
 * 2. Si la page fait moins de 3 fois la hauteur de l'écran, arrêter.
 * 3. Recommencer l'opération si la page est redimensionnée (ResizeObserver).
 * 4. Quand l'Utilisateur dépasse le seuil de 3 fois la hauteur de l'écran, faire apparaître le bouton.
 * 5. Le faire disparaître quand il retourne en-deça.
 * 6. Au clic dessus, aller sur l'ancre au sommet de la page et faire disparaître le bouton.
 */

const E = {
  BOUTON_MENU_MOBILE: recupereEleOuLeve<HTMLButtonElement>(SELECTEUR_BOUTON_MENU_MOBILE),
  BOUTON_RETOUR_SOMMET: recupereEleOuLeve<HTMLButtonElement>("#bouton-retour-haut"),
  CORPS_HTML: recupereEleOuLeve<HTMLBodyElement>("body"),
  IMAGE_BOUTON: recupereEleOuLeve<HTMLImageElement>("#bouton-retour-haut img"),
};

/** Le ratio minimum hauteur de page/hauteur de la fenêtre à atteindre pour que le Bouton soit nécessaire. */
const RATIO_MINIMUM_PAGE_PAR_FENETRE = 3;

/** Le ratio actuel hauteur de page/hauteur de la fenêtre. */
let ratioActuel = E.CORPS_HTML.getBoundingClientRect().height / window.innerHeight;
/** La position actuelle du défilement vertical dans la page. */
let defilementY = window.scrollY;
/** Indique qu'une étape de rafraîchissement via `requestAnimationFrame` a déjà été demandée. */
let etapePlanifiee = false;

/**
 * Retourne la position du défilement vertical dans la page.
 *
 * @returns Un nombre en pixels.
 */
const majDefilementY = (): number => window.scrollY;

/**
 * @param estVisible
 */
const majVisibiliteBouton = (estVisible: boolean): void => {
  if (estVisible) {
    E.BOUTON_RETOUR_SOMMET.setAttribute(ATTRIBUT_ACTIF, "");
    E.BOUTON_RETOUR_SOMMET.setAttribute(ATTRIBUT_TABINDEX, "99");
    E.IMAGE_BOUTON.setAttribute(ATTRIBUT_ARIA_HIDDEN, "false");
  } else {
    E.BOUTON_RETOUR_SOMMET.removeAttribute(ATTRIBUT_ACTIF);
    E.BOUTON_RETOUR_SOMMET.setAttribute(ATTRIBUT_TABINDEX, "-2");
    E.IMAGE_BOUTON.setAttribute(ATTRIBUT_ARIA_HIDDEN, "true");
  }
  etapePlanifiee = false;
};

/**
 * Initialise le cycle de mises à jour du Bouton en fonction de la position du défilement dans la page et le recalcul
 * de ces données au redimensionnement de la fenêtre.
 *
 * @returns void
 */
const initialiseObservationFenetre = (): void => {
  window.addEventListener("scroll", (): void => {
    // Met à jour la valeur du défilement vertical dans la page
    defilementY = majDefilementY();
    // Vérifie que le Ratio soit le bon
    if (ratioActuel < RATIO_MINIMUM_PAGE_PAR_FENETRE) return;
    // Attend la prochaine étape
    if (etapePlanifiee) return;

    etapePlanifiee = true;
    requestAnimationFrame((): void =>
      majVisibiliteBouton(defilementY > (window.innerHeight * RATIO_MINIMUM_PAGE_PAR_FENETRE))
    );
  });

  new ResizeObserver((entrees: Array<ResizeObserverEntry>): void => {
    pipe(
      arrayHead(entrees),
      optionTap((_): void => {
        ratioActuel = E.CORPS_HTML.getBoundingClientRect().height / window.innerHeight;
        defilementY = majDefilementY();
      }),
    );
  }).observe(E.CORPS_HTML);

  // Déclenche la première mise à jour
  majVisibiliteBouton(defilementY > window.innerHeight * RATIO_MINIMUM_PAGE_PAR_FENETRE);
};

/**
 * Navigue au sommet de la page et focus sur le premier élément de la page qui peut le recevoir.
 *
 * @returns void
 */
const initialiseBoutonRetourSommet = (): void => {
  E.BOUTON_RETOUR_SOMMET.addEventListener("click", (evenement: Event): void => {
    evenement.preventDefault();

    window.scrollTo({ behavior: "smooth", top: 0 });
    E.BOUTON_MENU_MOBILE.focus({ preventScroll: true });
  });
};

document.addEventListener("DOMContentLoaded", (): void => {
  initialiseBoutonRetourSommet();
  initialiseObservationFenetre();
});
